import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Users, Film, Award, TrendingUp, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { mockData } from '../data/mock';
import OptimizedImage from '../components/OptimizedImage';

const Home = () => {
  const { company, services, projects, stats } = mockData;

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Video Background */}
        <div className="absolute inset-0 z-0 bg-black">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-30"
            style={{ filter: 'brightness(0.6)' }}
          >
            <source src="/Exclusive_Event_Shadow_Dance_Video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <div className="max-w-4xl mx-auto space-y-8">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight overflow-visible">
              <span className="gold-gradient-text block mb-8 pb-2">Prestige Events</span>
              <span className="text-silver text-4xl md:text-5xl lg:text-6xl font-light relative block pb-12">
                {company.tagline}
                <div className="absolute bottom-3 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-gold to-transparent opacity-80"></div>
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-0.5 bg-gold opacity-60"></div>
              </span>
            </h1>
            <p className="text-silver text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              {company.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-6 text-lg rounded-none transition-all duration-300 flex items-center gap-2">
                  Start Your Journey
                  <ArrowRight size={20} />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-6 text-lg rounded-none transition-all duration-300">
                  View Portfolio
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-gold rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-black border-t border-b border-gold/20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center space-y-2 group hover:scale-105 transition-transform duration-300">
                <h3 className="text-4xl md:text-5xl font-bold gold-gradient-text">{stat.value}</h3>
                <p className="text-gray-400 text-sm tracking-wide">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-gold text-sm tracking-widest uppercase">About Us</p>
                <h2 className="text-4xl md:text-5xl font-bold text-silver leading-tight">
                  Elevating Events to
                  <span className="gold-gradient-text"> Art</span>
                </h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">
                With over 12 years of excellence in luxury event management, Prestige Events has become 
                Thailand's most trusted name in creating unforgettable experiences. From intimate gatherings 
                to grand galas, we bring your vision to life with precision and elegance.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Award className="text-gold" size={32} />
                  <h4 className="text-silver font-semibold">Award-Winning</h4>
                  <p className="text-gray-400 text-sm">25+ industry awards for excellence</p>
                </div>
                <div className="space-y-2">
                  <TrendingUp className="text-gold" size={32} />
                  <h4 className="text-silver font-semibold">Proven Track Record</h4>
                  <p className="text-gray-400 text-sm">500+ successful events</p>
                </div>
              </div>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-gold text-gold hover:bg-gold hover:text-black px-6 py-3 rounded-none transition-all duration-300">
                  Learn More About Us
                </Button>
              </Link>
            </div>
            <div className="relative h-[500px]">
              <OptimizedImage
                src="/buffet.avif"
                alt="Luxury Events"
                className="rounded-sm h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-gold/5 rounded-sm -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Our Services</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Comprehensive Event
              <span className="gold-gradient-text"> Solutions</span>
            </h2>
            <p className="text-gray-300 text-lg">
              From conceptualization to execution, we offer end-to-end services for every occasion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon === 'Sparkles' ? Sparkles : service.icon === 'Users' ? Users : Film;
              const serviceLink = `/services/${service.id === 1 ? 'events-exhibitions' : service.id === 2 ? 'talent-booking' : 'media-production'}`;
              return (
                <Link key={service.id} to={serviceLink} className="block">
                  <Card className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group overflow-hidden cursor-pointer h-full">
                    <div className="relative h-64 overflow-hidden">
                      <OptimizedImage
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-grey via-dark-grey/50 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <IconComponent className="text-gold" size={32} />
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <h3 className="text-2xl font-bold text-silver group-hover:text-gold transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed">{service.description}</p>
                      <div className="inline-flex items-center text-gold group-hover:gap-2 transition-all duration-300 text-sm font-semibold">
                        Learn More <ArrowRight size={16} className="ml-1" />
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16 gap-6">
            <div className="space-y-4">
              <p className="text-gold text-sm tracking-widest uppercase">Recent Work</p>
              <h2 className="text-4xl md:text-5xl font-bold text-silver">
                Featured
                <span className="gold-gradient-text"> Projects</span>
              </h2>
            </div>
            <Link to="/portfolio">
              <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-6 py-3 rounded-none transition-all duration-300">
                View All Projects
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              projects.find(p => p.image === "/PC19.jpg"),
              projects.find(p => p.image === "/PC17.jpg"),
              projects.find(p => p.image === "/yp 3 Ranveer Singh.jpg"),
              projects.find(p => p.image === "/postcards series.jpg")
            ].filter(Boolean).map((project) => (
              <div key={project.id} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-sm aspect-[3/4]">
                  <OptimizedImage
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
                    <p className="text-gold text-xs tracking-wider uppercase">{project.category}</p>
                    <h3 className="text-silver text-lg font-semibold">{project.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-black border-t border-gold/20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Heart className="text-gold mx-auto" size={48} />
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Let's Create Something
              <span className="gold-gradient-text"> Extraordinary</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Whether it's an intimate celebration or a grand corporate event, we're here to make your vision a reality.
            </p>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-10 py-6 text-lg rounded-none transition-all duration-300 flex items-center gap-2 mx-auto">
                Start Planning Today
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;